import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Youtube, Loader2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { NewEpisode } from "@/components/podcast/NewEpisode";
import { EpisodeList } from "@/components/podcast/EpisodeList";

const redirect = () => `${window.location.origin}/admin/podcast`;

const YouTubeCard = ({ yt, reload }) => {
  const connect = async () => { try { const { data } = await api.post("/api/podcast/youtube/auth-url", { redirect_uri: redirect() }); window.location.href = data.url; } catch (e) { toast.error(errMsg(e)); } };
  const off = async () => { if (!window.confirm("Disconnect YouTube?")) return; await api.delete("/api/podcast/youtube"); reload(); };
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-wrap items-center gap-3" data-testid="yt-card">
      <Youtube className="w-6 h-6 text-red-600" />
      <div className="flex-1 min-w-[220px] text-sm">{yt.connected ? <p data-testid="yt-connected">Connected to <b>{yt.channel || "your channel"}</b>. Upload any finished episode with its button below.</p>
        : yt.configured ? <p data-testid="yt-ready">Connect your YouTube channel to upload episodes with one click.</p>
          : <p data-testid="yt-not-configured">YouTube needs Google sign-in keys before you can connect. Episodes save and play here in the meantime.</p>}</div>
      {yt.connected ? <button onClick={off} className="h-9 px-4 rounded-full border text-xs font-bold" data-testid="yt-disconnect">Disconnect</button>
        : <button onClick={connect} disabled={!yt.configured} className="h-9 px-4 rounded-full bg-red-600 text-white text-xs font-bold disabled:opacity-40" data-testid="yt-connect">Connect YouTube</button>}
    </section>
  );
};

export default function PodcastStudioPage() {
  const [rows, setRows] = useState(null);
  const [yt, setYt] = useState({});
  const [sp, setSp] = useSearchParams();
  const did = useRef(false);
  const load = useCallback(() => Promise.all([api.get("/api/podcast/episodes").then(r => setRows(r.data)), api.get("/api/podcast/youtube/status").then(r => setYt(r.data))]), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const fresh = (r) => Date.now() - new Date(r.created_at).getTime() < 3 * 3600 * 1000; if (!rows?.some(r => r.youtube?.status === "uploading" || (r.status === "recording" && fresh(r)))) return; const t = setInterval(load, 5000); return () => clearInterval(t); }, [rows, load]);
  useEffect(() => {
    const code = sp.get("code"), state = sp.get("state");
    if (!code || !state || did.current) return;
    did.current = true;
    api.post("/api/podcast/youtube/exchange", { code, state, redirect_uri: redirect() }).then(r => toast.success(`YouTube connected${r.data.channel ? `: ${r.data.channel}` : ""}`)).catch(e => toast.error(errMsg(e))).finally(() => { setSp({}); load(); });
  }, [sp, setSp, load]);
  return (
    <div className="pt-20 pb-20 min-h-screen bg-[#f6f5f1]" data-testid="podcast-studio"><div className="max-w-6xl mx-auto px-4 space-y-5">
      <div><p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-600">Marketing</p><h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">Podcast studio</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-3xl">Record shop owner stories and testimonials, then choose which ones loop on the homepage and which go to YouTube.</p></div>
      <YouTubeCard yt={yt} reload={load} />
      <NewEpisode onDone={load} />
      {rows ? <EpisodeList rows={rows} yt={yt} onChange={load} /> : <Loader2 className="w-5 h-5 animate-spin" />}
    </div></div>
  );
}
